





















































import { Component, Vue, Prop, ProvideReactive, Ref } from 'vue-property-decorator';
import EmployeeForm from '@/components/Employee/EmployeeForm.vue';
import EmployeeTaxCardsTableForm from '@/components/Employee/EmployeeTaxCardsTableForm.vue';
import WorkRelationsTableForm from '@/components/Employee/WorkRelationsTableForm.vue';
import { GQL_EmployeeUpdate } from '@/queries/global';
import { SmartQuery } from 'vue-apollo-decorator';
import { EMPLOYEE_FORM_UNIT_SETTINGS } from '@/queries/Employee';
import { GQL_employee } from '@/queries/__generated__/employee';
import DeepCopy from '@/decorators/deep-copy';
import TabTitle from '@/utils/tabtitle';
import unitPaycheckLineTemplateRequiredGuard from '@/guards/unit-paycheck-line-template-required-guard';
import PermissionService from '@/utils/permission-service';
import { UnsavedChanges } from '@/decorators/unsaved-changes';
import i18n from '@/i18n';
import { GQL_employeeFormUnitSettings, GQL_employeeFormUnitSettings_currentUnit_unitSetting_unitPaycheckSetting } from '@/queries/__generated__/employeeFormUnitSettings';
import { postEmployeeCreateHooks, sortWorkRelations, GQL_EmployeeForm } from '@/app/Employee';
import gql from 'graphql-tag';
import { employeeFragments } from '@/queries/employeeFragments';

const GET_EMPLOYEE = gql`
  query employee($id: ID!) {
    employee(id: $id) {
      ...employeeFragments,
    }
  }
  ${employeeFragments}
`;

const UPDATE_EMPLOYEE = gql`
  mutation updateEmployee($employee: EmployeeUpdate!) {
    updateEmployee(input: { employee: $employee }) {
      employee {
        id
      }
    }
  }
`;

type WithUndefinedUser<T> = T & {
  user: undefined;
};

@Component({
  components: {
    EmployeeForm,
    EmployeeTaxCardsTableForm,
    WorkRelationsTableForm,
  },
  beforeRouteEnter: (to, from, next) => {
    unitPaycheckLineTemplateRequiredGuard(to, from, next);
  },
})
export default class EmployeesEdit extends Vue {
  @Prop({ type: Boolean, required: true }) private isNewEmployee!: boolean; // comes from router

  @SmartQuery<EmployeesEdit, GQL_employeeFormUnitSettings>({
    query: EMPLOYEE_FORM_UNIT_SETTINGS,
    update: data => data.currentUnit.unitSetting.unitPaycheckSetting,
  }) public unitPaycheckSetting?: GQL_employeeFormUnitSettings_currentUnit_unitSetting_unitPaycheckSetting;

  @SmartQuery<EmployeesEdit, GQL_employee, { id: string }>({
    query: GET_EMPLOYEE,
    skip() { return !this.unitPaycheckSetting },
    fetchPolicy: 'no-cache',
    variables() { return { id: this.$route.params.id } },
    update: data => ({
      ...data.employee!,
      unit: undefined,
      country: undefined,
      countryCode: data.employee!.country.code,
      phoneCountry: undefined,
      phoneCountryCode: data.employee!.phoneCountry?.code || '',
      workRelations: sortWorkRelations(data.employee!.workRelations).map(workRelation => ({
        ...workRelation,
        subUnitId: workRelation.subUnit.id,
        occupationId: workRelation.occupation?.id || '',
      })),
    }),
    result({ data }) {
      TabTitle.setFields({ name: `${data.employee!.firstName} ${data.employee!.lastName}` });

      if (this.isNewEmployee) {
        postEmployeeCreateHooks(this.originalEmployee!, this.unitPaycheckSetting!);
        this.resetFormDirty();
        this.$router.push({
         name: 'EmployeesEdit',
         params: { id: this.$route.params.id },
       });
      }
    },
  }) private originalEmployee!: GQL_EmployeeForm;

  @DeepCopy('originalEmployee')
  private employee!: GQL_EmployeeForm;

  private UPDATE_EMPLOYEE = UPDATE_EMPLOYEE;
  private updateInProgress = false;

  private save() {
    this.updateInProgress = true;

    return this.$apollo.mutate({
      mutation: UPDATE_EMPLOYEE,
      variables: { employee: this.cleanupUIDs(this.employee) },
    })
      .finally(() => this.updateInProgress = false);
  }

  @Ref() public readonly employeeForm?: EmployeeForm;

  private employeeFormValid = true;
  private taxCardsFormValid = true;
  private workRelationsFormValid = true;

  @UnsavedChanges<EmployeesEdit>({
    messages: {
      // i18n.t used instead of this.$t because function is async and component calling it might be destroyed, making 'this' undefined
      entity: i18n.t('Employee.employee'),
    },
    saveAndContinue() {
      return this.save().then(() => {
        this.$sMessage.success(i18n.t('Message.saveSuccess'));
        return true;
      }).catch(() => {
        return false;
      });
    },
  }) private formDirty = false;

  @ProvideReactive()
  private get editable(): boolean {
    return PermissionService.check('canEditEmployees');
  }

  private PermissionService = PermissionService;

  private cleanupUIDs(employee: GQL_EmployeeForm): WithUndefinedUser<GQL_EmployeeUpdate> {
    return {
      ...employee,
      user: undefined,
      employeeTaxCards: employee.employeeTaxCards!.map(taxCard => ({
        ...taxCard,
        file: taxCard.file && 'signedBlobId' in taxCard.file ? undefined : taxCard.file,
        $_UID: undefined,
        $_expandableId: undefined,
      })),
      workRelations: employee.workRelations!.map(workRelation => ({
        ...workRelation,
        subUnit: undefined,
        occupation: undefined,
        sentToAltinn: undefined,
        endDateSentToAltinn: undefined,
        leaveAndLayoffs: workRelation.leaveAndLayoffs!.map(leaveLayoff => ({
          ...leaveLayoff,
          isSentToAltinn: undefined,
          isEndDateSentToAltinn: undefined,
          $_UID: undefined,
        })),
        $_UID: undefined,
        $_expandableId: undefined,
      })),
    };
  }

  private employeeUpdated() {
    this.$sMessage.success(this.$t('Message.saveSuccess'));
    this.$apollo.queries.originalEmployee.refetch().then(this.resetFormDirty);
    this.employeeForm!.setSsnEditing();
  }

  private resetFormDirty() {
    this.employeeForm?.form?.resetDirty();
  }
}
